/**
 * KrazyBeads · booking app (hybrid)
 *
 * Vanilla JS, no deps. Talks to the Cloudflare Worker (which proxies
 * SimplyBook). Renders KB-styled service picker → date strip → start–end
 * slot grid → confirm CTA. After confirm, mounts SimplyBook's widget with
 * a `navigate` deep-link so it lands directly on the form + payment step.
 *
 * Reads config from data-* attributes on the root element.
 */

const I18N = {
  zh: {
    loading: '加载中…',
    error: '出错了，请稍后重试',
    duration: '时长',
    minutes: '分钟',
    hours: '小时',
    deposit: '预约金',
    open: '营业',
    closed: '店休',
    full: '已约满',
    past: '已截止',
    slotFull: '此时段已约满',
    slotPast: '此时段已截止',
    noSlots: '当日无可用时段',
  },
  en: {
    loading: 'Loading…',
    error: 'Something went wrong. Please try again.',
    duration: 'Duration',
    minutes: 'min',
    hours: 'hr',
    deposit: 'Deposit',
    open: 'Open',
    closed: 'Closed',
    full: 'Fully booked',
    past: 'Booking closed',
    slotFull: 'Fully booked',
    slotPast: 'Booking closed',
    noSlots: 'No available slots',
  },
};

class BookingApp {
  constructor(root) {
    this.root = root;
    this.workerUrl = root.dataset.workerUrl;
    // The studio's full "book in advance" window (mirrors the SimplyBook
    // backend setting). We pull the whole span once, then page through it a
    // week at a time — so widening it later is just a settings change, with no
    // week-by-week probing and nothing about the backend limit hardcoded here.
    this.maxDaysAhead = parseInt(root.dataset.daysAhead || '35', 10);
    this.pageSize = 7; // days shown per page (one week)
    this.locale = (root.dataset.locale || 'zh').toLowerCase().startsWith('en') ? 'en' : 'zh';
    this.t = I18N[this.locale];
    this.simplybookUrl = root.dataset.simplybookUrl || 'https://krazybeads.simplybook.net';
    this.widgetTheme = root.dataset.widgetTheme || 'creative';
    this.widgetThemeSettings = {
      timeline_show_end_time: '1',
      timeline_modern_display: 'as_slots',
      timeline_hide_unavailable: '1',
      hide_past_days: '0',
      hide_img_mode: '0',
      show_sidebar: '1',
      display_item_mode: 'list',
      sb_base_color: root.dataset.colorPrimary || '#1B232C',
      booking_nav_bg_color: root.dataset.colorPrimary || '#1B232C',
      body_bg_color: root.dataset.colorBg || '#F8F4EA',
      dark_font_color: root.dataset.colorTextDark || '#1B232C',
      light_font_color: root.dataset.colorTextLight || '#F8F4EA',
      btn_color_1: root.dataset.colorButton || '#6E6A4A',
      sb_busy: root.dataset.colorBusy || '#D6CFB0',
      sb_available: root.dataset.colorAvailable || '#F2EBDB',
    };

    // Sane fallbacks if SimplyBook returns nothing.
    this.slotInterval = 30;
    this.fallbackOpen = '11:00';
    this.fallbackClose = '18:00';

    this.state = {
      services: [],
      units: [],
      selectedServiceId: null,
      selectedUnitId: null,
      selectedDate: null,
      selectedSlot: null,
      weekOffset: 0, // which 7-day page of the window is shown
      windowDates: [], // every ISO date across the bookable window
      lastBookableIso: null, // furthest date with a free slot → caps paging
      availabilityByDate: {},
      workCalendar: {}, // { 'YYYY-MM-DD': true } when SimplyBook marks the day off (店休)
      shopHours: null, // { open: 'HH:MM', close: 'HH:MM' }, derived from API matrix
      currency: '$',
      error: null,
    };

    this.els = {
      services: root.querySelector('[data-slot="services"]'),
      serviceStep: root.querySelector('[data-slot="step-services"]'),
      datetime: root.querySelector('[data-slot="datetime"]'),
      weekNav: root.querySelector('[data-slot="week-nav"]'),
      dates: root.querySelector('[data-slot="dates"]'),
      slots: root.querySelector('[data-slot="slots"]'),
      confirmCta: root.querySelector('[data-slot="confirm-cta"]'),
      widgetStep: root.querySelector('[data-slot="widget-step"]'),
      widgetFrame: root.querySelector('[data-slot="widget-frame"]'),
      widgetLoading: root.querySelector('[data-slot="widget-loading"]'),
      widgetBack: root.querySelector('[data-slot="widget-back"]'),
      message: root.querySelector('[data-slot="message"]'),
    };

    if (this.els.widgetBack) {
      this.els.widgetBack.addEventListener('click', () => this.backToSlotPick());
    }

    this.init();
  }

  async rpc(method, params) {
    const res = await fetch(this.workerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method, params: params || [] }),
    });
    const data = await res.json();
    if (data.error) {
      const msg = typeof data.error === 'string' ? data.error : (data.error.message || 'RPC error');
      const err = new Error(msg);
      err.fullError = data.error;
      err.method = method;
      err.params = params;
      console.error(`[KB-booking] RPC '${method}' failed:`, data.error, 'with params:', params);
      throw err;
    }
    return data.result;
  }

  async init() {
    if (!this.workerUrl) {
      this.showMessage(this.t.error + ' (Worker URL 未配置)', 'error');
      return;
    }
    try {
      const [services, units, info] = await Promise.all([
        this.rpc('getEventList'),
        this.rpc('getUnitList'),
        this.rpc('getCompanyInfo').catch(() => null),
      ]);

      console.log('[KB-booking] getEventList:', services);
      console.log('[KB-booking] getUnitList:', units);
      console.log('[KB-booking] getCompanyInfo:', info);

      this.state.services = this.normalizeServices(services);
      this.state.units = this.normalizeUnits(units);
      if (info && info.currency) this.state.currency = info.currency;

      this.renderServices();
    } catch (err) {
      console.error(err);
      this.showMessage(this.t.error + ' (' + err.message + ')', 'error');
    }
  }

  normalizeServices(raw) {
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : Object.values(raw);
    return arr
      .filter((s) => s && (s.is_active === undefined || s.is_active == 1 || s.is_active === true))
      .map((s) => ({
        id: String(s.id),
        name: s.name,
        description: s.description || '',
        duration: parseInt(s.duration, 10) || 30,
        price: s.price ? parseFloat(s.price) : null,
      }));
  }

  normalizeUnits(raw) {
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : Object.values(raw);
    return arr
      .filter((u) => u && (u.is_active === undefined || u.is_active == 1 || u.is_active === true))
      .map((u) => ({ id: String(u.id), name: u.name }));
  }

  renderServices() {
    const grid = this.els.services;
    if (!this.state.services.length) {
      grid.innerHTML = `<div class="kb-booking-app__empty">${this.t.error}</div>`;
      return;
    }
    grid.innerHTML = this.state.services
      .map((s) => {
        const { eyebrow, main } = parseServiceName(s.name);
        const durationDisplay = formatDuration(s.duration, this.t);
        const priceDisplay = s.price ? `${this.state.currency || '$'}${s.price}` : '';
        return `
          <button type="button" class="kb-booking-app__service-card" data-service-id="${s.id}">
            ${eyebrow ? `<div class="kb-booking-app__service-eyebrow">${escapeHtml(eyebrow)}</div>` : ''}
            <div class="kb-booking-app__service-name">${escapeHtml(main)}</div>
            ${s.description ? `<div class="kb-booking-app__service-desc">${sanitizeRichText(s.description)}</div>` : ''}
            <div class="kb-booking-app__service-divider"></div>
            <div class="kb-booking-app__service-footer">
              <span class="kb-booking-app__service-duration">${durationDisplay}</span>
              ${priceDisplay ? `<span class="kb-booking-app__service-price">${priceDisplay}</span>` : ''}
            </div>
          </button>
        `;
      })
      .join('');

    grid.querySelectorAll('[data-service-id]').forEach((btn) => {
      btn.addEventListener('click', () => this.pickService(btn.dataset.serviceId));
    });
  }

  async pickService(serviceId) {
    this.state.selectedServiceId = serviceId;
    this.state.selectedSlot = null;
    this.state.selectedDate = null;
    if (this.els.confirmCta) this.els.confirmCta.classList.add('hidden');

    this.els.services
      .querySelectorAll('[data-service-id]')
      .forEach((btn) =>
        btn.classList.toggle('is-selected', btn.dataset.serviceId === serviceId)
      );

    if (!this.state.selectedUnitId && this.state.units[0]) {
      this.state.selectedUnitId = this.state.units[0].id;
    }

    this.els.datetime.classList.remove('hidden');

    await this.loadAvailability();
    this.renderDates();
    this.scrollTo(this.els.datetime);
  }

  // Pull the *entire* bookable window (today → today + maxDaysAhead) up front,
  // then page through it locally a week at a time. getStartTimeMatrix only ever
  // returns genuinely-bookable days, so the furthest date it hands back doubles
  // as our "book in advance" ceiling — no hardcoded backend limit, no per-page
  // round-trips. It rejects long spans ("Period too long" — the 7-day call the
  // original code used was always safe), so we request the window in page-sized
  // chunks and merge them.
  async loadAvailability() {
    const today = new Date();
    const windowDates = [];
    for (let i = 0; i < this.maxDaysAhead; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      windowDates.push(formatDateISO(d));
    }
    this.state.windowDates = windowDates;
    this.state.weekOffset = 0;

    // Split into <=pageSize-day spans — getStartTimeMatrix 422s on long ranges.
    const chunks = [];
    for (let i = 0; i < windowDates.length; i += this.pageSize) {
      chunks.push(windowDates.slice(i, i + this.pageSize));
    }
    const unitParam = this.state.selectedUnitId ? [this.state.selectedUnitId] : null;

    this.els.slots.innerHTML = `<div class="kb-booking-app__loading">${this.t.loading}</div>`;

    try {
      // getStartTimeMatrix(dateFrom, dateTo, eventId, [unitId], count=1) gives
      // bookable start times; getWorkCalendar tells us which days are genuine
      // days off (店休) vs working days that just have no slots left (已约满).
      const [matrices, workCalendar] = await Promise.all([
        Promise.all(
          chunks.map((c) =>
            this.rpc('getStartTimeMatrix', [c[0], c[c.length - 1], this.state.selectedServiceId, unitParam, 1])
          )
        ),
        this.fetchWorkCalendar(windowDates).catch((e) => {
          // A calendar failure must not nuke availability — degrade by
          // treating every unbookable day as 已约满 rather than 店休.
          console.warn('[KB-booking] getWorkCalendar failed:', e);
          return {};
        }),
      ]);
      // Chunks span disjoint date ranges, so a shallow merge rebuilds the matrix.
      const merged = Object.assign({}, ...matrices.map((m) => m || {}));
      console.log('[KB-booking] getStartTimeMatrix merged:', merged);
      console.log('[KB-booking] workCalendar:', workCalendar);
      this.state.availabilityByDate = this.normalizeAvailability(merged);
      this.state.workCalendar = workCalendar;
      this.state.shopHours = this.deriveShopHours();
      this.state.lastBookableIso = this.findLastBookableIso();
      console.log('[KB-booking] derived shopHours:', this.state.shopHours);
      console.log('[KB-booking] lastBookableIso:', this.state.lastBookableIso);
    } catch (err) {
      console.error(err);
      this.state.availabilityByDate = {};
      this.state.workCalendar = {};
      this.state.shopHours = null;
      this.state.lastBookableIso = null;
      this.showMessage(this.t.error + ' (' + err.message + ')', 'error');
    }
  }

  // Derive open/close from the union of all available start times across the
  // returned date range. With no bookings, min(start) == open of day,
  // max(start) + duration == close of day. With bookings near the edges this
  // collapses inward — but since we span 7 days, if any single day in the
  // window has no edge-booking, that day's edges become the "true" hours.
  deriveShopHours() {
    const allStarts = [];
    for (const set of Object.values(this.state.availabilityByDate)) {
      for (const t of set) allStarts.push(t);
    }
    if (!allStarts.length) {
      return { open: this.fallbackOpen, close: this.fallbackClose };
    }
    allStarts.sort();
    const minStart = allStarts[0];
    const maxStart = allStarts[allStarts.length - 1];
    const service = this.state.services.find((s) => s.id === this.state.selectedServiceId);
    const closeMinutes = parseHHMM(maxStart) + (service ? service.duration : 60);
    return {
      open: minStart,
      close: minutesToHHMM(closeMinutes),
    };
  }

  normalizeAvailability(matrix) {
    // SimplyBook returns { 'YYYY-MM-DD': { 'HH:MM:SS': true, ... } } or
    // { 'YYYY-MM-DD': ['HH:MM:SS', ...] } — handle both
    const out = {};
    if (!matrix || typeof matrix !== 'object') return out;
    for (const [date, value] of Object.entries(matrix)) {
      let times = [];
      if (Array.isArray(value)) times = value;
      else if (value && typeof value === 'object') times = Object.keys(value);
      out[date] = new Set(times.map((t) => t.slice(0, 5))); // 'HH:MM'
    }
    return out;
  }

  // Fetch SimplyBook's work calendar across the visible window and reduce it to
  // a { 'YYYY-MM-DD': isDayOff } map. getWorkCalendar is keyed by (year, month),
  // so a window straddling a month boundary needs one call per month.
  async fetchWorkCalendar(dates) {
    const seen = new Set();
    const months = [];
    for (const iso of dates) {
      const ym = iso.slice(0, 7); // 'YYYY-MM'
      if (seen.has(ym)) continue;
      seen.add(ym);
      const [y, m] = ym.split('-').map((n) => parseInt(n, 10));
      months.push([y, m]);
    }
    const calendars = await Promise.all(
      months.map(([y, m]) => this.rpc('getWorkCalendar', [y, m]))
    );
    const out = {};
    for (const cal of calendars) {
      if (!cal || typeof cal !== 'object') continue;
      for (const [date, info] of Object.entries(cal)) {
        out[date] = !!(info && (info.is_day_off === '1' || info.is_day_off === 1 || info.is_day_off === true));
      }
    }
    return out;
  }

  // True when the date's whole bookable window is already in the past — a past
  // calendar day, or today once every slot's start time is behind us. Such a
  // day is 已截止 (closed for the day), not 已约满.
  isDatePast(iso) {
    const now = new Date();
    const todayIso = formatDateISO(now);
    if (iso < todayIso) return true;
    if (iso > todayIso) return false;
    const service = this.state.services.find((s) => s.id === this.state.selectedServiceId);
    const hours = this.state.shopHours || { open: this.fallbackOpen, close: this.fallbackClose };
    const slots = generateSlotGrid(hours.open, hours.close, this.slotInterval, service ? service.duration : 30);
    return !slots.some((s) => new Date(`${iso}T${s.start}:00`) >= now);
  }

  renderDates() {
    this.renderWeekNav();

    const container = this.els.dates;
    const workCal = this.state.workCalendar || {};
    const weekDates = this.currentWeekDates();
    container.innerHTML = weekDates
      .map((iso) => {
        const d = new Date(iso + 'T00:00:00');
        const weekday = d.toLocaleDateString(this.locale === 'zh' ? 'zh-CN' : 'en-US', {
          weekday: 'short',
        });
        const day = d.getDate();
        const month = d.toLocaleDateString(this.locale === 'zh' ? 'zh-CN' : 'en-US', {
          month: 'short',
        });
        // Four states: 店休 = genuine day off (per SimplyBook, e.g. Mon/Fri),
        // 已截止 = a working day whose bookable window has already passed (e.g.
        // today after closing), 已约满 = still open but no slots left, 营业 =
        // bookable. Only true days off read as 店休.
        const hasSlots = !!(this.state.availabilityByDate[iso] && this.state.availabilityByDate[iso].size);
        const dayOff = workCal[iso] === true;
        const past = !dayOff && !hasSlots && this.isDatePast(iso);
        const disabled = dayOff || !hasSlots;
        const statusLabel = dayOff
          ? this.t.closed
          : hasSlots
          ? this.t.open
          : past
          ? this.t.past
          : this.t.full;
        const cls = disabled
          ? ' is-closed' + (dayOff ? '' : past ? ' is-past' : ' is-full')
          : '';
        return `
          <button type="button" class="kb-booking-app__date${cls}"
                  data-date="${iso}"${disabled ? ' disabled' : ''}>
            <span class="kb-booking-app__date-weekday">${weekday}</span>
            <span class="kb-booking-app__date-day">${day}</span>
            <span class="kb-booking-app__date-month">${month}</span>
            <span class="kb-booking-app__date-status">${statusLabel}</span>
          </button>
        `;
      })
      .join('');

    container.querySelectorAll('[data-date]:not([disabled])').forEach((btn) => {
      btn.addEventListener('click', () => this.pickDate(btn.dataset.date));
    });

    // Auto-select the first bookable day on *this* page. A week with none (all
    // booked / days off) clears the slot grid with a hint instead.
    const firstAvail = weekDates.find(
      (d) => this.state.availabilityByDate[d] && this.state.availabilityByDate[d].size
    );
    if (firstAvail) {
      this.pickDate(firstAvail);
    } else {
      this.state.selectedDate = null;
      this.els.slots.innerHTML = `<div class="kb-booking-app__empty">${this.t.noSlots}</div>`;
    }
  }

  // The 7-day slice currently shown. The final page may be shorter when the
  // window isn't a clean multiple of 7.
  currentWeekDates() {
    const all = this.state.windowDates || [];
    const start = this.state.weekOffset * this.pageSize;
    return all.slice(start, start + this.pageSize);
  }

  // Furthest date in the window that still has a free slot — caps how far the
  // "next week" arrow can page. Null when nothing is bookable at all.
  findLastBookableIso() {
    const dates = this.state.windowDates || [];
    for (let i = dates.length - 1; i >= 0; i--) {
      const set = this.state.availabilityByDate[dates[i]];
      if (set && set.size) return dates[i];
    }
    return null;
  }

  // Arrows flank a date-range label. "prev" is dead on the first page; "next"
  // dies once the following page would start past the last bookable day, so the
  // user can't page into empty future weeks (or beyond the backend's window).
  renderWeekNav() {
    const nav = this.els.weekNav;
    if (!nav) return;
    const weekDates = this.currentWeekDates();
    if (!weekDates.length) {
      nav.innerHTML = '';
      return;
    }

    const rangeLabel = this.formatRange(weekDates[0], weekDates[weekDates.length - 1]);
    const prevDisabled = this.state.weekOffset === 0;
    const nextFirstIso = (this.state.windowDates || [])[(this.state.weekOffset + 1) * this.pageSize];
    const lastBookable = this.state.lastBookableIso;
    const nextDisabled = !nextFirstIso || !lastBookable || nextFirstIso > lastBookable;

    const prevLabel = this.locale === 'zh' ? '上一周' : 'Previous week';
    const nextLabel = this.locale === 'zh' ? '下一周' : 'Next week';
    nav.innerHTML = `
      <button type="button" class="kb-booking-app__week-arrow" data-week="prev"
              aria-label="${prevLabel}"${prevDisabled ? ' disabled' : ''}>‹</button>
      <span class="kb-booking-app__week-label">${rangeLabel}</span>
      <button type="button" class="kb-booking-app__week-arrow" data-week="next"
              aria-label="${nextLabel}"${nextDisabled ? ' disabled' : ''}>›</button>
    `;

    const prev = nav.querySelector('[data-week="prev"]');
    const next = nav.querySelector('[data-week="next"]');
    if (prev) prev.addEventListener('click', () => this.changeWeek(-1));
    if (next) next.addEventListener('click', () => this.changeWeek(1));
  }

  formatRange(firstIso, lastIso) {
    const loc = this.locale === 'zh' ? 'zh-CN' : 'en-US';
    const opts = { month: this.locale === 'zh' ? 'long' : 'short', day: 'numeric' };
    const a = new Date(firstIso + 'T00:00:00').toLocaleDateString(loc, opts);
    const b = new Date(lastIso + 'T00:00:00').toLocaleDateString(loc, opts);
    return `${a} – ${b}`;
  }

  changeWeek(delta) {
    const pages = Math.ceil((this.state.windowDates || []).length / this.pageSize);
    const next = this.state.weekOffset + delta;
    if (next < 0 || next > pages - 1) return;
    this.state.weekOffset = next;
    this.state.selectedSlot = null;
    if (this.els.confirmCta) this.els.confirmCta.classList.add('hidden');
    this.renderDates();
  }

  pickDate(iso) {
    this.state.selectedDate = iso;
    this.state.selectedSlot = null;

    this.els.dates
      .querySelectorAll('[data-date]')
      .forEach((b) => b.classList.toggle('is-selected', b.dataset.date === iso));

    this.renderSlots();
  }

  renderSlots() {
    const service = this.state.services.find((s) => s.id === this.state.selectedServiceId);
    const available = this.state.availabilityByDate[this.state.selectedDate] || new Set();
    const hours = this.state.shopHours || { open: this.fallbackOpen, close: this.fallbackClose };

    const slots = generateSlotGrid(hours.open, hours.close, this.slotInterval, service.duration);
    if (!slots.length) {
      this.els.slots.innerHTML = `<div class="kb-booking-app__empty">${this.t.noSlots}</div>`;
      return;
    }

    const now = new Date();

    this.els.slots.innerHTML = slots
      .map((s) => {
        const enabled = available.has(s.start);
        // A disabled slot is either already past (today, before now) or booked
        // out. Past slots aren't "full" — they get a distinct 已截止 label.
        const past = !enabled && new Date(`${this.state.selectedDate}T${s.start}:00`) < now;
        const stateLabel = enabled ? '' : past ? this.t.slotPast : this.t.slotFull;
        const stateClass = enabled ? '' : past ? ' is-disabled is-past' : ' is-disabled is-full';
        return `
          <button type="button" class="kb-booking-app__slot${stateClass}"
                  data-start="${s.start}" data-end="${s.end}"${enabled ? '' : ' disabled aria-disabled="true"'}>
            <span class="kb-booking-app__slot-time">${s.start} — ${s.end}</span>
            ${stateLabel ? `<span class="kb-booking-app__slot-state">${stateLabel}</span>` : ''}
          </button>
        `;
      })
      .join('');

    this.els.slots.querySelectorAll('[data-start]:not([disabled])').forEach((btn) => {
      btn.addEventListener('click', () => this.pickSlot(btn.dataset.start, btn.dataset.end));
    });
  }

  pickSlot(start, end) {
    this.state.selectedSlot = { start, end };
    this.els.slots
      .querySelectorAll('[data-start]')
      .forEach((b) => b.classList.toggle('is-selected', b.dataset.start === start));
    this.renderConfirmCta();
    this.scrollTo(this.els.confirmCta);
  }

  // After slot pick, show a confirmation summary and a CTA button.
  // Click → mount SimplyBook widget with a navigate deep-link.
  renderConfirmCta() {
    if (!this.els.confirmCta) return;
    const service = this.state.services.find((s) => s.id === this.state.selectedServiceId);
    if (!service) return;
    const priceLabel = service.price ? `${this.state.currency || '$'}${service.price}` : '';

    this.els.confirmCta.classList.remove('hidden');
    this.els.confirmCta.innerHTML = `
      <div class="kb-booking-app__confirm-summary">
        <div class="kb-booking-app__confirm-row">
          <span>服务</span><strong>${escapeHtml(service.name)}</strong>
        </div>
        <div class="kb-booking-app__confirm-row">
          <span>时段</span><strong>${this.state.selectedDate} ${this.state.selectedSlot.start} – ${this.state.selectedSlot.end}</strong>
        </div>
        ${priceLabel ? `<div class="kb-booking-app__confirm-row"><span>预约金</span><strong>${priceLabel}</strong></div>` : ''}
      </div>
      <button type="button" class="kb-booking-app__confirm-btn" data-slot="confirm-btn">确认信息并支付</button>
      <p class="kb-booking-app__confirm-note">点击后会进入安全表单，填写联系方式并完成押金支付。5 分钟内未完成会自动释放时段。</p>
    `;
    const btn = this.els.confirmCta.querySelector('[data-slot="confirm-btn"]');
    if (btn) btn.addEventListener('click', () => this.launchWidget());
  }

  loadWidgetJs() {
    if (window.SimplybookWidget) return Promise.resolve();
    if (this._widgetLoading) return this._widgetLoading;
    this._widgetLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://widget.simplybook.net/v2/widget/widget.js';
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error('widget.js failed to load'));
      document.head.appendChild(s);
    });
    return this._widgetLoading;
  }

  async launchWidget() {
    if (this.els.serviceStep) this.els.serviceStep.classList.add('hidden');
    if (this.els.datetime) this.els.datetime.classList.add('hidden');
    if (this.els.confirmCta) this.els.confirmCta.classList.add('hidden');
    if (this.els.widgetStep) this.els.widgetStep.classList.remove('hidden');
    // Show "加载中…" placeholder until the iframe finishes its initial load.
    if (this.els.widgetLoading) this.els.widgetLoading.classList.remove('is-hidden');
    this.scrollTo(this.els.widgetStep);

    try {
      await this.loadWidgetJs();
    } catch (e) {
      console.error('[KB-booking] widget.js load failed:', e);
      if (this.els.widgetLoading) this.els.widgetLoading.classList.add('is-hidden');
      this.showMessage('预约组件加载失败，请刷新重试', 'error');
      return;
    }

    // The inner SimplyBook SPA registers a Backbone route:
    //   book(/location/:l)(/category/:c)(/service/:s)(/count/:n)(/addons/:a)(/date/:d)(/time/:t)(/provider/:p)
    // Navigating to that route deep-links past every step we've already done.
    // `predefined` only locks service/provider — it cannot pre-select date/time —
    // so the route is what actually skips the datetime step.
    const serviceId = this.state.selectedServiceId;
    const providerId = this.state.selectedUnitId;
    const date = this.state.selectedDate;
    const time = this.state.selectedSlot.start + ':00';
    const navigateTo =
      `book/service/${serviceId}/date/${date}/time/${time}/provider/${providerId}`;

    console.log('[KB-booking] launching widget, navigate:', navigateTo);

    const frame = this.els.widgetFrame;
    if (frame) frame.innerHTML = '';

    try {
      // eslint-disable-next-line no-undef
      new SimplybookWidget({
        widget_type: 'iframe',
        url: this.simplybookUrl,
        theme: this.widgetTheme,
        theme_settings: this.widgetThemeSettings,
        timeline: 'flexible',
        datepicker: 'top_calendar',
        is_rtl: false,
        container_id: frame ? frame.id : undefined,
        app_config: {
          clear_session: 1,
          allow_switch_to_ada: 0,
          // Lock service + provider so the user can't accidentally change them
          // on the way through the form.
          predefined: {
            service: serviceId,
            provider: providerId,
          },
        },
        navigate: navigateTo,
      });

      // Hide the loading placeholder once SB's inner SPA reports readiness.
      // `appReady` is the most reliable signal — iframe.load fires when the
      // HTML document loads, but SB's JS may still be initialising and the
      // form not yet rendered, so iframe.load alone causes a flash. Listen
      // for postMessage with event:'appReady' (sent by SB widget). Also keep
      // an iframe.load + 600ms fallback and an 8s ultimate fallback.
      const iframe = frame ? frame.querySelector('iframe') : null;
      if (this.els.widgetLoading) {
        const hideLoading = () => {
          if (this.els.widgetLoading) this.els.widgetLoading.classList.add('is-hidden');
        };
        const onMessage = (event) => {
          if (iframe && event.source !== iframe.contentWindow) return;
          if (event.data && event.data.event === 'appReady') {
            hideLoading();
            window.removeEventListener('message', onMessage);
          }
        };
        window.addEventListener('message', onMessage);
        if (iframe) {
          iframe.addEventListener('load', () => setTimeout(hideLoading, 600), { once: true });
        }
        setTimeout(hideLoading, 8000);
      }
    } catch (e) {
      console.error('[KB-booking] widget init failed:', e);
      if (this.els.widgetLoading) this.els.widgetLoading.classList.add('is-hidden');
      if (frame) frame.innerHTML = `<div style="padding:60px;text-align:center;color:#A8503E;">加载失败：${escapeHtml(e.message || '')}</div>`;
    }
  }

  backToSlotPick() {
    if (this.els.widgetStep) this.els.widgetStep.classList.add('hidden');
    if (this.els.serviceStep) this.els.serviceStep.classList.remove('hidden');
    if (this.els.datetime) this.els.datetime.classList.remove('hidden');
    if (this.els.confirmCta) this.els.confirmCta.classList.remove('hidden');
    // Wipe the iframe (so a fresh widget mounts on next launch) and re-show
    // the loading placeholder so it's visible during the next launch's load.
    if (this.els.widgetFrame) this.els.widgetFrame.innerHTML = '';
    if (this.els.widgetLoading) this.els.widgetLoading.classList.remove('is-hidden');
    this.scrollTo(this.els.datetime);
  }

  showMessage(text, kind) {
    this.els.message.className = `kb-booking-app__message kb-booking-app__message--${kind}`;
    this.els.message.textContent = text;
    this.els.message.classList.remove('hidden');
  }

  scrollTo(el) {
    if (!el) return;
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }
}

function generateSlotGrid(openHHMM, closeHHMM, intervalMin, serviceDuration) {
  const open = parseHHMM(openHHMM);
  const close = parseHHMM(closeHHMM);
  const slots = [];
  for (let m = open; m + serviceDuration <= close; m += intervalMin) {
    slots.push({ start: minutesToHHMM(m), end: minutesToHHMM(m + serviceDuration) });
  }
  return slots;
}

function parseHHMM(s) {
  const [h, m] = s.split(':').map((n) => parseInt(n, 10));
  return h * 60 + (m || 0);
}

function minutesToHHMM(total) {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDateISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Split a service name like "In-store Shop 选购成品 (订金)" into:
//   { eyebrow: "In-store Shop", main: "选购成品" }
// — leading non-CN text is the eyebrow, the first CN run is the main name,
// and any trailing parenthetical (e.g. "(订金)") is stripped.
function parseServiceName(fullName) {
  if (!fullName) return { eyebrow: '', main: '' };
  const cnRegex = /[一-龥]/;
  const idx = fullName.search(cnRegex);
  if (idx === -1) return { eyebrow: '', main: fullName };
  const eyebrow = idx === 0 ? '' : fullName.slice(0, idx).trim();
  const rest = fullName.slice(idx).replace(/\s*\([^)]+\)\s*$/, '').trim();
  return { eyebrow, main: rest };
}

// Format minutes as "X 小时" — fractional when not a whole hour
// (30 → "0.5 小时", 60 → "1 小时", 90 → "1.5 小时").
function formatDuration(minutes, t) {
  if (!minutes && minutes !== 0) return '';
  const hours = minutes / 60;
  const display = hours === Math.floor(hours) ? hours.toString() : hours.toFixed(1);
  return `${display} ${t.hours}`;
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Render merchant-provided HTML (from SimplyBook description field) but
// strip inline style/class/font attributes so it inherits the KB design.
function sanitizeRichText(html) {
  return String(html == null ? '' : html)
    .replace(/\s(style|class|face|color|size|align)\s*=\s*"[^"]*"/gi, '')
    .replace(/\s(style|class|face|color|size|align)\s*=\s*'[^']*'/gi, '')
    .replace(/<\/?font[^>]*>/gi, '');
}

document.querySelectorAll('[data-kb-booking-app]').forEach((el) => new BookingApp(el));
